import { useEffect, useState } from 'react';
import { Settings2, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import {
  DEFAULT_ONBOARDING_ASSIGNMENT_POLICIES,
  normalizeOnboardingAssignmentPolicies,
  type OnboardingAssignmentPolicy,
} from './onboarding-assignment-policy';

export default function OnboardingAssignmentPoliciesPage() {
  const [items, setItems] = useState<OnboardingAssignmentPolicy[]>(DEFAULT_ONBOARDING_ASSIGNMENT_POLICIES);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadPolicies() {
    setLoading(true);
    setError(null);
    try {
      const row = await api.get<{ items?: OnboardingAssignmentPolicy[] }>('/onboarding/assignment-policies');
      setItems(normalizeOnboardingAssignmentPolicies(row.items));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assignment policies');
      setItems(DEFAULT_ONBOARDING_ASSIGNMENT_POLICIES);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadPolicies();
  }, []);

  function updateRole(policyId: string, index: number, value: string) {
    setItems((current) => current.map((policy) => {
      if (policy.id !== policyId) return policy;
      const next = [...policy.rolePriority];
      next[index] = value.trim().toLowerCase();
      return { ...policy, rolePriority: next.filter(Boolean) };
    }));
  }

  async function savePolicies() {
    setSaving(true);
    setError(null);
    try {
      await api.put('/onboarding/assignment-policies', {
        items: items.map((policy) => ({
          policyKey: policy.id,
          label: policy.label,
          appliesTo: policy.appliesTo,
          escalationTarget: policy.escalationTarget,
          rolePriority: policy.rolePriority,
          fallback: policy.fallback,
          isActive: policy.isActive ?? true,
          metadata: policy.metadata ?? {},
        })),
      });
      toast.success('Assignment policies saved');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save assignment policies');
      toast.error(err instanceof Error ? err.message : 'Failed to save assignment policies');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings2 className="h-6 w-6 text-primary" />
            Onboarding Assignment Policies
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            配置 onboarding brief、sales follow-up、delivery review、support review 的默认角色优先级与 fallback。
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => void loadPolicies()} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => void savePolicies()} disabled={saving}>
            {saving ? 'Saving…' : 'Save Policies'}
          </Button>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Policy error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        {items.map((policy) => (
          <Card key={policy.id}>
            <CardHeader>
              <CardTitle className="text-base">{policy.label}</CardTitle>
              <div className="text-xs text-muted-foreground">
                {policy.appliesTo} · escalation target {policy.escalationTarget} · fallback {policy.fallback}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {[0, 1, 2].map((index) => (
                <div key={index}>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                    Priority {index + 1}
                  </div>
                  <Input
                    value={policy.rolePriority[index] ?? ''}
                    onChange={(event) => updateRole(policy.id, index, event.target.value)}
                    placeholder="manager / admin / supervisor"
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
